import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/teacher-courses/TeacherCoursesAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { TeacherCoursesFilter } from "./teacher-courses-filter/TeacherCoursesFilter";
import { TeacherCoursesTable } from "./teacher-courses-table/TeacherCoursesTable";
import { useTeacherCoursesUIContext } from "./TeacherCoursesUIContext";

export function TeacherCoursesCard() {
  // Teacher Courses Card UI Context
  const teacherCoursesUIContext = useTeacherCoursesUIContext();
  const teacherCoursesUIProps = useMemo(() => {
    return {
      ids: teacherCoursesUIContext.ids,
      setIds: teacherCoursesUIContext.setIds,
      queryParams: teacherCoursesUIContext.queryParams,
      openNewSectionPage: teacherCoursesUIContext.openNewSectionPage
    };
  }, [teacherCoursesUIContext]);
    
  // Getting curret state of Teacher Course list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.teacherCourses }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Teacher Courses Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);
    
  /** Belli Bir Öğretmen Dersi Listesini Excele Aktarır */
  const exportTeacherCoursesToExcel = () => {
    dispatch(actions.getAllTeacherCourses(teacherCoursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [15, 25, 30, 30, 45];
      const columns = ["Sınıf Düzeyi", "Sınıf", "Öğretmen", "Branş", "Ders"];
      entities.forEach(entity => rows.push([entity.gradeLevelName, entity.section, entity.teacher, entity.branch, entity.course]));
      const filename = teacherCoursesUIProps.queryParams.term + " - " + (teacherCoursesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğretmen Ders Listesi";
      const sheetname = "Öğretmen Ders Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });    
  }

  /** Belli Bir Öğretmen Dersi Listesini PDF e Aktarır */
  const exportTeacherCoursesToPDF = () => {
    dispatch(actions.getAllTeacherCourses(teacherCoursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Sınıf Düzeyi",
          rows: entities.map(entity => entity.gradeLevelName)
        },
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Öğretmen",
          rows: entities.map(entity => entity.teacher)
        },
        {
          header: "Branş",
          rows: entities.map(entity => entity.branch)
        },
        {
          header: "Ders",
          rows: entities.map(entity => entity.course)
        }
      ];

      const filename = "Öğretmen Ders Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğretmen Dersi Listesini Yazdırma Penceresini Açar */
  const printTeacherCourses = () => {
    dispatch(actions.getAllTeacherCourses(teacherCoursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = teacherCoursesUIProps.queryParams.term + " - " + (teacherCoursesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğretmen Ders Listesi";
      const properties = ["Sınıf Düzeyi", "Sınıf", "Öğretmen", "Branş", "Ders"];

      entities.forEach(entity => {
        data.push({
          "Sınıf Düzeyi": entity.gradeLevelName,
          "Sınıf": entity.section,
          "Öğretmen": entity.teacher,
          "Branş": entity.branch,
          "Ders": entity.course          
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Öğretmen Dersi Listesini Panoya Kopyalar */
  const copyTeacherCourses = () => {
    dispatch(actions.getAllTeacherCourses(teacherCoursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Sınıf Düzeyi", "Sınıf", "Öğretmen", "Branş", "Ders"];
      const header = teacherCoursesUIProps.queryParams.term + " - " + (teacherCoursesUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğretmen Ders Listesi";
      entities.forEach(entity => rows.push([entity.gradeLevelName, entity.section, entity.teacher, entity.branch, entity.course]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Öğretmen Dersi Listesini Siler */
  const deleteTeacherCourses = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteTeacherCourses(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // clear selections list
        teacherCoursesUIProps.setIds([]);
        // refresh list after deletion
        dispatch(actions.getTeacherCourses(teacherCoursesUIProps.queryParams));        
      }, 2500);
    }
  };
  
  const title = <>Öğretmen Ders Listesi{teacherCoursesUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{teacherCoursesUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isEditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportTeacherCoursesToExcel}
                exportToPDF={exportTeacherCoursesToPDF}
                print={printTeacherCourses}
                copy={copyTeacherCourses}
              />
            }
            {
              isEditionAuthorized &&
              <FormButton
                path="Navigation/Check"
                title="Sınıf ve Ders Seçme"
                method={teacherCoursesUIProps.openNewSectionPage}
              />
            }              
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteTeacherCourses(teacherCoursesUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <TeacherCoursesFilter />
        <TeacherCoursesTable
          deleteTeacherCourses={deleteTeacherCourses}
        />
      </CardBody>
    </Card>
  );
}